/**
 * 全局 UI 偏好（主题、密度、面板开关、toast）。
 *
 * 约定：主题与密度**只**通过 <html data-theme / data-density> 生效，
 * 组件不允许读取「当前是不是深色」然后写 if 分支 —— 浅/深差异由 tokens 的语义层吸收。
 * localStorage 的键与 index.html 的首屏引导脚本共享（ams.theme / ams.density）。
 */

import { create } from "zustand";

export type ThemeMode = "light" | "dark" | "system";
export type DensityMode = "comfortable" | "compact" | "dense";

/**
 * 设计方向固定为 `polaris`（console 定稿：Shopify Polaris 语言，见 styles/tokens.css 的
 * `[data-direction="polaris"]`）。方向与主题/密度正交 —— 它只覆盖颜色性格与形状
 * （圆角、阴影、描边强度），不碰 `--row-height` / `--fs-*`，所以密度切换不会被它打坏。
 *
 * 这里不做成用户可选项：全站只有一套已定稿的语言，留一个切换开关只会制造
 * 「线上到底是哪一套」的歧义。要改方向就改这个常量。
 */
export const AMS_DIRECTION = "polaris";

export const THEME_STORAGE_KEY = "ams.theme";
export const DENSITY_STORAGE_KEY = "ams.density";

export interface ToastAction {
  readonly label: string;
  readonly run: () => void;
}

export interface Toast {
  readonly id: string;
  readonly kind: "info" | "success" | "warning" | "danger";
  readonly title: string;
  readonly detail: string | null;
  readonly action: ToastAction | null;
  readonly ttlMs: number;
  readonly createdAt: number;
}

export interface PushToastInput {
  readonly kind: Toast["kind"];
  readonly title: string;
  readonly detail?: string | null;
  readonly action?: ToastAction | null;
  readonly ttlMs?: number;
}

interface UiState {
  readonly theme: ThemeMode;
  readonly density: DensityMode;
  readonly commandOpen: boolean;
  readonly helpOpen: boolean;
  /**
   * 「设置」大弹窗（2026-10-04 第三轮第 4 条）。
   *
   * 为什么放在全局 store 而不是某个页面的 state：设置入口在**外壳**（侧栏左下角），
   * 而弹窗必须盖在**当前工作页面**之上且不卸载它（筛选 / 滚动位置不能丢）。
   * 只有外壳持有这个开关，才能做到「关闭 = 回到原页面」而不是「导航回去」。
   */
  readonly settingsOpen: boolean;
  readonly perfHudOpen: boolean;
  readonly toasts: readonly Toast[];
  setTheme: (theme: ThemeMode) => void;
  setDensity: (density: DensityMode) => void;
  setCommandOpen: (open: boolean) => void;
  setHelpOpen: (open: boolean) => void;
  setSettingsOpen: (open: boolean) => void;
  setPerfHudOpen: (open: boolean) => void;
  pushToast: (input: PushToastInput) => string;
  dismissToast: (id: string) => void;
}

let toastSequence = 0;

export function prefersDark(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );
}

export function resolveTheme(mode: ThemeMode): "light" | "dark" {
  if (mode === "system") return prefersDark() ? "dark" : "light";
  return mode;
}

export function readStoredTheme(): ThemeMode {
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
    return raw === "light" || raw === "dark" || raw === "system" ? raw : "system";
  } catch {
    return "system";
  }
}

export function readStoredDensity(): DensityMode {
  try {
    const raw = window.localStorage.getItem(DENSITY_STORAGE_KEY);
    return raw === "comfortable" || raw === "compact" || raw === "dense" ? raw : "compact";
  } catch {
    return "compact";
  }
}

function persist(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* 隐私模式下写不进去，忽略：本会话仍然生效 */
  }
}

export function applyTheme(mode: ThemeMode): void {
  document.documentElement.setAttribute("data-theme", resolveTheme(mode));
}

export function applyDensity(mode: DensityMode): void {
  document.documentElement.setAttribute("data-density", mode);
}

/**
 * 方向只需要写一次：index.html 的首屏引导脚本已经写过（防 FOUC），
 * 这里再写一次是为了覆盖 **hash 路由的单文件构建**（file:// / 沙箱 iframe 下
 * index.html 的属性可能与产品构建不同）以及测试环境。
 */
export function applyDirection(direction: string = AMS_DIRECTION): void {
  document.documentElement.setAttribute("data-direction", direction);
}

export const useUiStore = create<UiState>()((set) => ({
  theme: readStoredTheme(),
  density: readStoredDensity(),
  commandOpen: false,
  helpOpen: false,
  settingsOpen: false,
  perfHudOpen: true,
  toasts: [],

  setTheme: (theme) => {
    persist(THEME_STORAGE_KEY, theme);
    applyTheme(theme);
    set({ theme });
  },

  setDensity: (density) => {
    persist(DENSITY_STORAGE_KEY, density);
    applyDensity(density);
    set({ density });
  },

  setCommandOpen: (commandOpen) => set({ commandOpen }),
  setHelpOpen: (helpOpen) => set({ helpOpen }),
  setSettingsOpen: (settingsOpen) => set({ settingsOpen }),
  setPerfHudOpen: (perfHudOpen) => set({ perfHudOpen }),

  pushToast: (input) => {
    toastSequence += 1;
    const id = `toast_${Date.now().toString(36)}_${toastSequence}`;
    const toast: Toast = {
      id,
      kind: input.kind,
      title: input.title,
      detail: input.detail ?? null,
      action: input.action ?? null,
      ttlMs: input.ttlMs ?? 5000,
      createdAt: Date.now(),
    };
    set((state) => ({ toasts: [...state.toasts, toast].slice(-4) }));
    return id;
  },

  dismissToast: (id) =>
    set((state) => ({ toasts: state.toasts.filter((toast) => toast.id !== id) })),
}));
